import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Hover tooltip in the app's own styling.
 *
 * The native `title` attribute is the thing this replaces: the browser decides
 * when it appears (roughly a second, unconfigurable) and how it looks (the OS
 * chrome, ignoring the theme), which on a dark UI reads as a bug. This opens
 * after {@link DELAY_MS}, uses the popover tokens, and follows the light/dark
 * theme like everything else.
 *
 * Deliberately small: portal-rendered so it escapes any `overflow: hidden`
 * ancestor, positioned above the trigger and flipped below when there is no
 * room, and `pointer-events-none` so it can never swallow the click meant for
 * the element underneath it. Keyboard focus opens it too, so the information is
 * not mouse-only.
 */

/** Short enough to feel instant, long enough not to flash while moving across. */
const DELAY_MS = 100;
const GAP = 8;
const EDGE = 8;

export function Tooltip({
  label,
  children,
  maxWidth = 260,
}: {
  /** Tooltip content. Nothing renders when this is empty. */
  label: ReactNode;
  /** The trigger — wrapped in an inline-flex span that owns the hover. */
  children: ReactNode;
  maxWidth?: number;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const timer = useRef<number | null>(null);

  function show() {
    if (timer.current !== null) return;
    timer.current = window.setTimeout(() => {
      timer.current = null;
      setOpen(true);
    }, DELAY_MS);
  }

  function hide() {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    setOpen(false);
  }

  // A pending timer outliving the component would fire setState on a dead node.
  useEffect(() => () => hide(), []);

  // Placed before paint, and only once the tip is mounted so its real size is
  // known — measuring a guess would make it jump on first show.
  useLayoutEffect(() => {
    if (!open || !anchorRef.current || !tipRef.current) return;
    const anchor = anchorRef.current.getBoundingClientRect();
    const tip = tipRef.current.getBoundingClientRect();

    const above = anchor.top - tip.height - GAP;
    const top = above >= EDGE ? above : anchor.bottom + GAP;

    let left = anchor.left + anchor.width / 2 - tip.width / 2;
    left = Math.max(EDGE, Math.min(left, window.innerWidth - tip.width - EDGE));

    setPos({ top, left });
  }, [open, label]);

  // Scrolling the message list would leave the tip stranded next to nothing.
  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [open]);

  if (!label) return <>{children}</>;

  return (
    <>
      <span
        ref={anchorRef}
        className="inline-flex"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocusCapture={show}
        onBlurCapture={hide}
      >
        {children}
      </span>
      {open &&
        createPortal(
          <div
            ref={tipRef}
            role="tooltip"
            // Rendered off-screen for the first frame: the layout effect needs
            // to measure it before it can be placed, and measuring it where it
            // will not be seen avoids a visible jump from 0,0.
            style={{
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              maxWidth,
              visibility: pos ? 'visible' : 'hidden',
            }}
            className="ui-popover-panel pointer-events-none fixed z-[90] px-2.5 py-1.5 text-xs leading-snug"
          >
            {label}
          </div>,
          document.body,
        )}
    </>
  );
}
